import React, { Suspense, lazy, useEffect, useState, useRef } from 'react'
import { BrowserRouter, Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom'

import CartInitializer from './components/CartInitializer'
import ReviewNotification from './components/ReviewNotification'
import { getReviewPrompt, dismissReviewPrompt } from './services/reviewService'
import ToastNotification from './components/ToastNotification'
import PhosphorIconInit from './helper/PhosphorIconInit'
import RouteScrollToTop from './helper/RouteScrollToTop'

import CustomerPrivateRoute from './components/CustomerPrivateRoute' // ...
import RequirePhoneNumber from './components/RequirePhoneNumber'
import RequireDeliveryAddress from './components/RequireDeliveryAddress'
import PushNotificationListener from './components/PushNotificationListener'
import LocationMismatchPrompt from './components/LocationMismatchPrompt'

import BottomNavBar from './components/BottomNavBar'

import { NotificationProvider } from './contexts/NotificationContext'
import { AppModeProvider } from './contexts/AppModeContext'
import { LocationProvider } from './contexts/LocationContext'

import { useGoogleMaps } from './hooks/useGoogleMaps'
import { useAuth } from './hooks/useAuth'
import { useAppMode } from './contexts/AppModeContext'
import LandingPage from './pages/LandingPage'
import { canonicalFor, robotsFor } from './utils/seoRoutes'

// Pages load on demand, so a visitor to the landing page does not download
// checkout, maps, charts and every other screen first.
const AccountLogin = lazy(() => import('./components/AccountLogin'))
const AccountRegister = lazy(() => import('./components/AccountRegister'))
const ReferralLandingPage = lazy(() => import('./pages/ReferralLandingPage'))
const CartPage = lazy(() => import('./pages/CartPage'))
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'))
const CompleteProfilePage = lazy(() => import('./pages/CompleteProfilePage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const HomePageOne = lazy(() => import('./pages/HomePageOne'))
const ProductDetailsPageTwo = lazy(() => import('./pages/ProductDetailsPageTwo'))
const VendorShop = lazy(() => import('./pages/VendorShop'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const CustomerProfilePage = lazy(() => import('./pages/CustomerProfilePage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const OTPVerificationPage = lazy(() => import('./pages/OTPVerificationPage'))
const OrdersPage = lazy(() => import('./pages/OrdersPage'))
const OrderTracking = lazy(() => import('./pages/OrderTracking'))
const RestaurantsPage = lazy(() => import('./pages/RestaurantsPage'))
const GroceriesPage = lazy(() => import('./pages/GroceriesPage'))
const CategoryProductsPage = lazy(() => import('./pages/CategoryProductsPage'))
const FlashSaleDetailsPage = lazy(() => import('./pages/FlashSaleDetailsPage'))
const OrderSuccess = lazy(() => import('./components/OrderSuccess'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage'))
const AddressFormPage = lazy(() => import('./pages/AddressFormPage'))
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'))
const PaymentFailed = lazy(() => import('./pages/PaymentFailed'))
const SearchPage = lazy(() => import('./pages/SearchPage'))

const headTag = (selector, tag, attrs) => {
  let el = document.head.querySelector(selector)
  if (!el) {
    el = document.createElement(tag)
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v))
    document.head.appendChild(el)
  }
  return el
}

// Robots and canonical URL for every route (see utils/seoRoutes). Without the
// canonical, every page inherited index.html's home-page URL.
const RouteRobots = () => {
  const { pathname } = useLocation()
  useEffect(() => {
    const url = canonicalFor(pathname)
    headTag('meta[name="robots"]', 'meta', { name: 'robots' }).setAttribute('content', robotsFor(pathname))
    headTag('link[rel="canonical"]', 'link', { rel: 'canonical' }).setAttribute('href', url)
    headTag('meta[property="og:url"]', 'meta', { property: 'og:url' }).setAttribute('content', url)
  }, [pathname])
  return null
}

// Older order links (/order/<id>, the app's own path) open the tracking page.
const OrderLinkRedirect = () => {
  const { orderId } = useParams()
  return <Navigate to={`/order-tracking/${encodeURIComponent(orderId)}`} replace />
}

// "/" is the landing page for visitors on the web; signed-in customers and
// the native app shells go straight to the shop, as before.
const HomeRoute = () => {
  const { token, user } = useAuth()
  const { isAppMode } = useAppMode()
  if (isAppMode || (token && user)) return <HomePageOne />
  return <LandingPage />
}

// Google Maps is heavy: load it up front only for a signed-in customer (who
// is on their way to addresses, checkout and tracking). Pages that draw a
// map still load it themselves when they open.
const SignedInExtras = () => {
  useGoogleMaps()
  return <LocationMismatchPrompt />
}

const PageFallback = () => (
  <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '50vh' }} role="status" aria-live="polite">
    <span className="spinner-border text-secondary" aria-hidden="true" />
    <span className="visually-hidden">Loading…</span>
  </div>
)

function App() {
  const { token } = useAuth() || {}
  const [prompt, setPrompt] = useState(null)
  const isMountedRef = useRef(true)
  const recheckTimerRef = useRef(null)

  const checkPrompt = async () => {
    // Only a signed-in customer has orders to review.
    if (!localStorage.getItem('token')) return
    try {
      const data = await getReviewPrompt()
      if (isMountedRef.current && data && data.show_review_prompt) {
        const key = `review-prompt-hidden-${data.order_id}`
        const ts = Number(localStorage.getItem(key) || 0)
        const now = Date.now()
        const HIDE_MS = 15 * 60 * 1000 // 15 minutes

        if (ts && now - ts < HIDE_MS) {
          // still within hide window; schedule a re-check after remaining time
          const remaining = HIDE_MS - (now - ts)
          scheduleRecheck(remaining)
          return
        }

        setPrompt(data)
      }
    } catch (err) {
      // ignore
    }
  }

  useEffect(() => {
    isMountedRef.current = true
    checkPrompt()
    return () => {
      isMountedRef.current = false
      if (recheckTimerRef.current) clearTimeout(recheckTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  const scheduleRecheck = ms => {
    if (recheckTimerRef.current) clearTimeout(recheckTimerRef.current)
    recheckTimerRef.current = setTimeout(async () => {
      await checkPrompt()
    }, ms)
  }

  const handlePromptDismiss = async () => {
    try {
      if (prompt?.order_id) await dismissReviewPrompt(prompt.order_id)
    } catch (err) {
      // ignore
    } finally {
      // persist hide timestamp so refresh won't show it for 15 minutes
      if (prompt?.order_id) {
        localStorage.setItem(`review-prompt-hidden-${prompt.order_id}`, String(Date.now()))
      }
      setPrompt(null)
      // re-check after 15 minutes to show again if server still wants to prompt
      scheduleRecheck(15 * 60 * 1000)
    }
  }

  const handlePromptAutoHide = () => {
    // persist hide timestamp so refresh won't show it for 15 minutes
    if (prompt?.order_id) {
      localStorage.setItem(`review-prompt-hidden-${prompt.order_id}`, String(Date.now()))
    }
    // Hide locally and re-check after 15 minutes
    setPrompt(null)
    scheduleRecheck(15 * 60 * 1000)
  }

  return (
    <BrowserRouter>
      <AppModeProvider>
        <LocationProvider>
          <NotificationProvider>
            <RouteScrollToTop />
            <RequirePhoneNumber />
            <RequireDeliveryAddress />
            <PushNotificationListener />
            {token && <SignedInExtras />}
            <RouteRobots />
            <PhosphorIconInit />
            <ToastNotification />
            <CartInitializer />
            {prompt && prompt.order_id && <ReviewNotification order={{ id: prompt.order_id }} onDismiss={handlePromptDismiss} onAutoHide={handlePromptAutoHide} />}
            <div id="app-content">
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  <Route
                    exact
                    path="/checkout"
                    element={
                      <CustomerPrivateRoute>
                        <CheckoutPage />
                      </CustomerPrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/order-success"
                    element={
                      <CustomerPrivateRoute>
                        <OrderSuccess />
                      </CustomerPrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/account"
                    element={
                      <CustomerPrivateRoute>
                        <CustomerProfilePage />
                      </CustomerPrivateRoute>
                    }
                  />
                  <Route exact path="/" element={<HomeRoute />} />
                  <Route exact path="/shop" element={<HomePageOne />} />
                  <Route exact path="/restaurants" element={<RestaurantsPage />} />
                  <Route exact path="/groceries" element={<GroceriesPage />} />
                  <Route exact path="/search" element={<SearchPage />} />
                  <Route exact path="/create-account" element={<AccountRegister />} />
                  <Route exact path="/otp-verification" element={<OTPVerificationPage />} />
                  <Route exact path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route exact path="/reset-password" element={<ResetPasswordPage />} />
                  <Route exact path="/account-login" element={<AccountLogin />} />
                  <Route exact path="/complete-profile" element={<CompleteProfilePage />} />
                  <Route exact path="/terms" element={<TermsPage />} />
                  <Route exact path="/privacy" element={<PrivacyPage />} />
                  <Route
                    exact
                    path="/orders"
                    element={
                      <CustomerPrivateRoute>
                        <OrdersPage />
                      </CustomerPrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/notifications"
                    element={
                      <CustomerPrivateRoute>
                        <NotificationsPage />
                      </CustomerPrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/notification-settings"
                    element={
                      <CustomerPrivateRoute>
                        <NotificationSettingsPage />
                      </CustomerPrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/addresses/new"
                    element={
                      <CustomerPrivateRoute>
                        <AddressFormPage />
                      </CustomerPrivateRoute>
                    }
                  />
                  <Route
                    exact
                    path="/addresses/:id/edit"
                    element={
                      <CustomerPrivateRoute>
                        <AddressFormPage />
                      </CustomerPrivateRoute>
                    }
                  />
                  <Route path="/addresses" element={<Navigate to="/account?tab=addresses" replace />} />
                  <Route exact path="/payment/success" element={<PaymentSuccess />} />
                  <Route exact path="/payment/failed" element={<PaymentFailed />} />
                  <Route
                    exact
                    path="/order-tracking/:orderId"
                    element={
                      <CustomerPrivateRoute>
                        <OrderTracking />
                      </CustomerPrivateRoute>
                    }
                  />
                  <Route exact path="/order/:orderId" element={<OrderLinkRedirect />} />
                  {/* Old demo link: it had no order to show, so it now opens My orders. */}
                  <Route exact path="/order-tracking-demo" element={<Navigate to="/orders" replace />} />
                  <Route exact path="/product-details/:id" element={<ProductDetailsPageTwo />} />
                  <Route exact path="/cart" element={<CartPage />} />
                  {/* Vendors are onboarded by the team: the old theme page now opens the contact form. */}
                  <Route exact path="/become-seller" element={<Navigate to="/contact?subject=Selling%20on%20Yum%20Express" replace />} />
                  <Route exact path="/contact" element={<ContactPage />} />
                  <Route exact path="/vendor-shop/:id" element={<VendorShop />} />
                  <Route exact path="/category/:categoryId" element={<CategoryProductsPage />} />
                  <Route exact path="/flash-sale/:saleId" element={<FlashSaleDetailsPage />} />
                  {/* Referral links the backend shares by SMS: /r/<CODE>. */}
                  <Route path="/r/:code" element={<ReferralLandingPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </div>
            <BottomNavBar />
          </NotificationProvider>
        </LocationProvider>
      </AppModeProvider>
    </BrowserRouter>
  )
}

export default App
